import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { LmsButton } from '@shared/ui'
import type { LearningTemplateResult } from './learning-template-contract'
import { getNumberedChoiceShortcutIndex } from './learning-template-shortcuts'
import {
  LearningTemplateExerciseResultBar,
  LearningTemplateIncorrectNote,
} from './learning-template-feedback'
import { LearningTemplateResultBar } from './learning-template-result-bar'

function shuffleTokens(tokens: readonly string[]) {
  const nextTokens = [...tokens]
  for (let index = nextTokens.length - 1; index > 0; index -= 1) {
    const targetIndex = Math.floor(Math.random() * (index + 1))
    const currentToken = nextTokens[index]
    nextTokens[index] = nextTokens[targetIndex]
    nextTokens[targetIndex] = currentToken
  }
  return nextTokens
}

function splitAnswerTokens(answer: string, mode: 'letters' | 'words') {
  if (!answer.trim()) {
    return []
  }
  return mode === 'letters' ? answer.replace(/\s+/g, '').split('') : answer.trim().split(/\s+/)
}

export function LearningSentenceRebuildTemplate({
  continuePending = false,
  continuePendingLabel,
  expectedAnswers,
  incorrectReviewText,
  isMobileOrTablet,
  mode,
  onContinue,
  onResult,
  sourceOwner,
  template,
}: {
  readonly continuePending?: boolean
  readonly continuePendingLabel?: ReactNode
  readonly expectedAnswers: readonly string[]
  readonly incorrectReviewText: string | null
  readonly isMobileOrTablet: boolean
  readonly mode: 'letters' | 'words'
  readonly onContinue: (result: LearningTemplateResult) => void
  readonly onResult?: (result: Exclude<LearningTemplateResult, 'skipped'>) => void
  readonly sourceOwner: string
  readonly template: '3' | '4'
}) {
  const expectedAnswer = expectedAnswers[0] ?? ''
  const [selectedResult, setSelectedResult] = useState<'correct' | 'incorrect' | null>(null)
  const [availableTokens, setAvailableTokens] = useState<string[]>(() =>
    shuffleTokens(splitAnswerTokens(expectedAnswer, mode)),
  )
  const [selectedTokens, setSelectedTokens] = useState<string[]>([])

  const handleAvailableToken = useCallback((tokenIndex: number) => {
    if (selectedResult) {
      return
    }

    const token = availableTokens[tokenIndex]
    if (!token) {
      return
    }

    const nextTokens = [...selectedTokens, token]
    setAvailableTokens((previous) => previous.filter((_, index) => index !== tokenIndex))
    setSelectedTokens(nextTokens)
    if (nextTokens.length === splitAnswerTokens(expectedAnswer, mode).length) {
      const answer = mode === 'letters' ? nextTokens.join('') : nextTokens.join(' ')
      const nextResult = answer === expectedAnswer ? 'correct' : 'incorrect'
      setSelectedResult(nextResult)
      onResult?.(nextResult)
    }
  }, [availableTokens, expectedAnswer, mode, onResult, selectedResult, selectedTokens])

  const handleKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    if (selectedResult) {
      return
    }

    const tokenIndex = getNumberedChoiceShortcutIndex(event, availableTokens.length)
    if (tokenIndex === null) {
      return
    }

    event.preventDefault()
    handleAvailableToken(tokenIndex)
  }, [availableTokens.length, handleAvailableToken, selectedResult])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardShortcut)
    return () => window.removeEventListener('keydown', handleKeyboardShortcut)
  }, [handleKeyboardShortcut])

  if (!expectedAnswer) {
    return (
      <LearningTemplateResultBar
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        finished={false}
        onNext={() => onContinue('correct')}
      />
    )
  }

  return (
    <>
      <div
        className="boxWrapAnswer legacySelfStudyRebuildExercise"
        data-legacy-owner={sourceOwner}
        data-legacy-template={template}
        data-qa={`learning-template-${template}-sentence-rebuild`}
      >
        <div className={`legacySelfStudyRebuildResult ${selectedResult ?? ''}`}>
          {selectedTokens.map((token, index) => (
            <LmsButton
              disabled={Boolean(selectedResult)}
              key={`selected-${token}-${index}`}
              onClick={() => {
                setSelectedTokens((previous) => previous.filter((_, tokenIndex) => tokenIndex !== index))
                setAvailableTokens((previous) => [...previous, token])
              }}
              type="button"
              variant="ghost"
            >
              {token}
            </LmsButton>
          ))}
        </div>
        {availableTokens.length > 0 ? (
          <div className="legacySelfStudyRebuildChoices">
            {availableTokens.map((token, index) => (
              <LmsButton
                aria-keyshortcuts={index < 9 && !selectedResult ? String(index + 1) : undefined}
                disabled={Boolean(selectedResult)}
                key={`available-${token}-${index}`}
                onClick={() => handleAvailableToken(index)}
                type="button"
                variant="ghost"
              >
                <span>{token}</span>
                <span className="legacySelfStudyExerciseSort">{index + 1}</span>
              </LmsButton>
            ))}
          </div>
        ) : null}
      </div>
      {selectedResult === 'incorrect' ? (
        <LearningTemplateIncorrectNote
          isMobileOrTablet={isMobileOrTablet}
          text={incorrectReviewText ?? expectedAnswer}
        />
      ) : null}
      {selectedResult ? (
        <LearningTemplateExerciseResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          onNext={() => onContinue(selectedResult)}
          result={selectedResult}
        />
      ) : null}
    </>
  )
}
